import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DatabaseBackup, Upload, Download, Trash2, RotateCcw, ShieldCheck, Clock, HardDrive, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { errText } from "@/lib/api";

const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(2)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDT = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Settings() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [creating, setCreating] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [preview, setPreview] = useState(null);
  const [password, setPassword] = useState("");
  const [restoreJob, setRestoreJob] = useState(null);
  const [jobState, setJobState] = useState(null);
  const [starting, setStarting] = useState(false);

  const { data, refetch } = useQuery({ queryKey: ["backups"], queryFn: async () => (await api.get("/backups")).data });

  useEffect(() => {
    if (!restoreJob) return;
    const t = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/backup/restore-status/${restoreJob}`);
        setJobState(j);
        if (j.done) {
          clearInterval(t);
          if (j.success) { qc.invalidateQueries(); refetch(); }
        }
      } catch { /* keep polling */ }
    }, 800);
    return () => clearInterval(t);
  }, [restoreJob]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadBackup = async (bid, name) => {
    try {
      const res = await api.get(`/backups/${bid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      const { data: rec } = await api.post("/backups");
      toast.success(`Backup completed successfully — ${fmtSize(rec.size)}, ${rec.total_records} records, ${rec.counts.files} files`);
      refetch();
      downloadBackup(rec.id, `anamta-noor-${rec.created_at.slice(0, 10)}.anamtanoor-backup`);
    } catch (e) { toast.error(errText(e)); } finally { setCreating(false); }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setInspecting(true);
    setUploadPct(0);
    try {
      const CHUNK = 5 * 1024 * 1024;
      const total = Math.max(Math.ceil(f.size / CHUNK), 1);
      const uploadId = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("file", f.slice(i * CHUNK, (i + 1) * CHUNK));
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", String(i));
        let ok = false;
        for (let attempt = 0; attempt < 3 && !ok; attempt++) {
          try {
            await api.post("/backup/upload-chunk", fd);
            ok = true;
          } catch (err) {
            if (attempt === 2) throw err;
            await new Promise((r) => setTimeout(r, 1000));
          }
        }
        setUploadPct(Math.round(((i + 1) / total) * 100));
      }
      const { data: res } = await api.post("/backup/finalize-upload", { upload_id: uploadId, total_chunks: total });
      setPassword("");
      setPreview({ ...res, filename: f.name });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setInspecting(false);
      setUploadPct(0);
    }
  };

  const startRestore = async (payload) => {
    setStarting(true);
    try {
      const { data: r } = await api.post("/backup/restore", { ...payload, password });
      setPreview(null);
      setJobState({ stage: "Preparing backup...", log: ["Preparing backup..."], done: false });
      setRestoreJob(r.job_id);
    } catch (err) { toast.error(errText(err)); } finally { setStarting(false); }
  };

  const [historyRestore, setHistoryRestore] = useState(null);

  const deleteBackup = async (bid) => {
    if (!window.confirm("Delete this backup permanently?")) return;
    try { await api.delete(`/backups/${bid}`); toast.success("Backup deleted"); refetch(); }
    catch { toast.error("Delete failed"); }
  };

  const setSchedule = async (v) => {
    try { await api.put("/backup/settings", { schedule: v }); toast.success(v === "off" ? "Automatic backup off" : `Automatic ${v} backup on`); refetch(); }
    catch { toast.error("Failed to save"); }
  };

  const m = preview?.manifest;
  const backups = data?.backups || [];
  const last = data?.last_backup;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Data Management — backup, export & restore</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="rounded-xl border bg-card p-5" data-testid="last-backup-card">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest"><Clock className="h-3.5 w-3.5" /> Last Backup</div>
          <p className="font-heading text-lg font-semibold mt-2">{last ? fmtDT(last.created_at) : "Never"}</p>
          {last && <p className="text-xs text-muted-foreground mt-1">{fmtSize(last.size)} · {last.total_records} records · {last.status}</p>}
        </div>
        <div className="rounded-xl border bg-card p-5" data-testid="backup-status-card">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest"><HardDrive className="h-3.5 w-3.5" /> Stored Backups</div>
          <p className="font-heading text-lg font-semibold mt-2">{backups.length}</p>
          <p className="text-xs text-muted-foreground mt-1">Kept safely in cloud storage</p>
        </div>
        <div className="rounded-xl border bg-card p-5" data-testid="auto-backup-card">
          <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest"><ShieldCheck className="h-3.5 w-3.5" /> Automatic Backup</div>
          <Select value={data?.auto_schedule || "off"} onValueChange={setSchedule}>
            <SelectTrigger data-testid="auto-backup-select" className="mt-2 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[["off", "Off"], ["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-xl border bg-card p-6 space-y-4">
        <div>
          <h2 className="font-heading font-semibold text-lg">Full Backup & Restore</h2>
          <p className="text-sm text-muted-foreground mt-1">
            A backup contains the complete ERP database — customers, orders, products, karigars, production, payments,
            business accounts, settings, photos, design images and PDF attachments — as one portable
            <span className="text-amber-500"> .anamtanoor-backup</span> file.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="create-backup-button" className="rounded-full" disabled={creating} onClick={createBackup}>
            {creating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <DatabaseBackup className="h-4 w-4 mr-1.5" />}
            {creating ? "Creating backup..." : "Create Full Backup"}
          </Button>
          <Button data-testid="import-backup-button" variant="outline" className="rounded-full" disabled={inspecting} onClick={() => fileRef.current?.click()}>
            {inspecting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
            {inspecting ? (uploadPct > 0 && uploadPct < 100 ? `Uploading... ${uploadPct}%` : "Validating file...") : "Import / Restore Backup"}
          </Button>
          <input ref={fileRef} type="file" accept=".anamtanoor-backup,.zip,application/zip" className="hidden" data-testid="import-backup-input" onChange={onImportFile} />
        </div>
      </div>

      <div>
        <h2 className="font-heading font-semibold text-lg mb-3">Backup History</h2>
        <div className="rounded-xl border bg-card overflow-x-auto" data-testid="backup-history-table">
          {backups.length === 0 ? (
            <p className="px-6 py-10 text-sm text-muted-foreground text-center">No backups yet — create your first backup above.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Backup</TableHead><TableHead>Created</TableHead><TableHead>Size</TableHead>
                  <TableHead>Version</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backups.map((b) => (
                  <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                    <TableCell className="max-w-[280px]"><span className="font-medium truncate block">{b.name}</span>
                      <span className="text-[11px] text-muted-foreground">{b.total_records} records · {b.counts?.files || 0} files</span></TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">{fmtDT(b.created_at)}</TableCell>
                    <TableCell className="text-muted-foreground">{fmtSize(b.size)}</TableCell>
                    <TableCell className="text-muted-foreground">v{b.backup_version}</TableCell>
                    <TableCell><span className="inline-flex px-2 py-0.5 rounded-full text-[11px] bg-emerald-500/10 text-emerald-500">{b.status}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Download" data-testid={`download-backup-${b.id}`}
                          onClick={() => downloadBackup(b.id, `anamta-noor-${b.created_at.slice(0, 10)}.anamtanoor-backup`)}><Download className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-500" title="Restore" data-testid={`restore-backup-${b.id}`}
                          onClick={() => { setPassword(""); setHistoryRestore(b); }}><RotateCcw className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" title="Delete" data-testid={`delete-backup-${b.id}`}
                          onClick={() => deleteBackup(b.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Import preview + confirm */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-lg" data-testid="restore-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Restore Backup?</DialogTitle>
            <DialogDescription>{preview?.filename}</DialogDescription>
          </DialogHeader>
          {m && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm rounded-lg border p-4">
                <span className="text-muted-foreground">Backup Date</span><span>{fmtDT(m.created_at)}</span>
                <span className="text-muted-foreground">ERP / Backup Version</span><span>v{m.erp_version} / v{m.backup_version}</span>
                <span className="text-muted-foreground">Schema Version</span><span>v{m.schema_version}</span>
                <span className="text-muted-foreground">Customers</span><span>{m.counts.customers}</span>
                <span className="text-muted-foreground">Orders / Products</span><span>{m.counts.orders} / {m.counts.products}</span>
                <span className="text-muted-foreground">Karigars</span><span>{m.counts.karigars}</span>
                <span className="text-muted-foreground">Business Entries</span><span>{m.counts.business_entries}</span>
                <span className="text-muted-foreground">Files</span><span>{m.counts.files}</span>
              </div>
              <p className="text-sm text-red-400 bg-red-500/10 rounded-lg p-3">
                Restoring this backup will replace the current ERP data with the data contained in this backup.
                A safety backup of your current data will be created automatically first.
              </p>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Enter your login password to confirm</Label>
                <Input data-testid="restore-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="rounded-full" onClick={() => setPreview(null)}>Cancel</Button>
                <Button data-testid="confirm-restore-button" className="rounded-full bg-red-600 hover:bg-red-700 text-white" disabled={!password || starting}
                  onClick={() => startRestore({ upload_id: preview.upload_id })}>
                  {starting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Restore Backup
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Restore from history confirm */}
      <Dialog open={!!historyRestore} onOpenChange={(v) => !v && setHistoryRestore(null)}>
        <DialogContent className="max-w-md" data-testid="history-restore-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Restore this backup?</DialogTitle>
            <DialogDescription>{historyRestore?.name} · {historyRestore && fmtDT(historyRestore.created_at)}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-red-400 bg-red-500/10 rounded-lg p-3">
            Restoring this backup will replace the current ERP data with the data contained in this backup.
            A safety backup will be created automatically first.
          </p>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Enter your login password to confirm</Label>
            <Input data-testid="history-restore-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setHistoryRestore(null)}>Cancel</Button>
            <Button data-testid="history-confirm-restore" className="rounded-full bg-red-600 hover:bg-red-700 text-white" disabled={!password || starting}
              onClick={async () => { await startRestore({ backup_id: historyRestore.id }); setHistoryRestore(null); }}>
              {starting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Restore Backup
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Restore progress */}
      <Dialog open={!!restoreJob} onOpenChange={(v) => { if (!v && jobState?.done) { setRestoreJob(null); setJobState(null); } }}>
        <DialogContent className="max-w-md" data-testid="restore-progress-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {!jobState?.done && <Loader2 className="h-4 w-4 animate-spin text-amber-500" />}
              {jobState?.done && jobState?.success && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
              {jobState?.done && !jobState?.success && <XCircle className="h-5 w-5 text-red-500" />}
              {jobState?.done ? (jobState.success ? "Restore Completed" : "Restore Failed") : "Restoring..."}
            </DialogTitle>
            <DialogDescription>{jobState?.done && !jobState?.success ? jobState?.error : "Please do not close this window"}</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border bg-muted/30 p-3 max-h-64 overflow-y-auto space-y-1.5 text-sm" data-testid="restore-progress-log">
            {(jobState?.log || []).map((l, i) => (
              <p key={i} className={i === (jobState?.log?.length || 0) - 1 && !jobState?.done ? "text-amber-500" : "text-muted-foreground"}>{l}</p>
            ))}
          </div>
          {jobState?.done && (
            <div className="flex justify-end">
              <Button data-testid="restore-done-button" className="rounded-full" onClick={() => { setRestoreJob(null); setJobState(null); if (jobState.success) window.location.reload(); }}>
                {jobState.success ? "Reload App" : "Close"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
